import {PriorityQueue} from "@cm/utils"

type RequestId = number

export type TileKindMap = {
    [kind: string]: {
        request: unknown;
        response: unknown
    }
}

type KindOf<TKinds extends TileKindMap> = keyof TKinds & string

export type TileResponse<TKinds extends TileKindMap> = TKinds[KindOf<TKinds>]["response"]

export type DiscriminatedTileRequest<TKinds extends TileKindMap> = {
    [K in KindOf<TKinds>]: {responseKind: K; request: TKinds[K]["request"]}
}[KindOf<TKinds>]

type PendingRequest<TKinds extends TileKindMap> = {
    request: DiscriminatedTileRequest<TKinds>
    abortSignal: AbortSignal
    resolve: (result: TileResponse<TKinds>) => void
    reject: (error: unknown) => void
}

export abstract class AbstractTileLoader<TKinds extends TileKindMap> {
    protected abstract startRequest(requestId: number, request: DiscriminatedTileRequest<TKinds>, abortSignal: AbortSignal): void

    dispose(): void {}

    async requestTile<K extends KindOf<TKinds>>(
        request: TKinds[K]["request"],
        responseKind: K,
        priority: number,
        abortSignal: AbortSignal,
    ): Promise<TKinds[K]["response"]> {
        return new Promise<TKinds[K]["response"]>((resolve, reject) => {
            const requestId = this.nextRequestId++
            this.pendingById.set(requestId, {
                // pendingById stores requests of possibly-different K side by side, so request and resolve are
                // widened to their unions. Sound because the request pairs this request's own responseKind with
                // its matching request type, and resolveRequest is only ever invoked (by this request's own
                // startRequest implementation) with a result matching that same kind - TypeScript can't
                // verify that correlation across a keyed map itself, hence the casts.
                request: {responseKind, request} as unknown as DiscriminatedTileRequest<TKinds>,
                abortSignal,
                resolve: resolve as (result: TileResponse<TKinds>) => void,
                reject,
            })
            this.queue.enqueue(requestId, priority)
            abortSignal.addEventListener("abort", () => this.cancel(requestId), {once: true})
            this.drain()
        })
    }

    get queueLength(): number {
        return this.queue.size
    }

    protected resolveRequest(requestId: RequestId, result: TileResponse<TKinds>): void {
        const request = this.pendingById.get(requestId)
        if (!request) {
            return
        }
        this.pendingById.delete(requestId)
        this.active--
        request.resolve(result)
        this.drain()
    }

    protected rejectRequest(requestId: RequestId, error: unknown): void {
        const request = this.pendingById.get(requestId)
        if (!request) {
            return
        }
        this.pendingById.delete(requestId)
        this.active--
        request.reject(error)
        this.drain()
    }

    private drain(): void {
        while (this.active < this.maxConcurrent) {
            const requestId = this.queue.dequeue()
            if (requestId === null) {
                break
            }
            const request = this.pendingById.get(requestId)
            if (!request) {
                throw new Error(`AbstractTileLoader: request ${requestId} missing`)
            }
            this.active++
            this.startRequest(requestId, request.request, request.abortSignal)
        }
    }

    private cancel(requestId: RequestId): void {
        const request = this.pendingById.get(requestId)
        if (!request) {
            return
        }
        this.pendingById.delete(requestId)
        if (this.queue.contains(requestId)) {
            this.queue.remove(requestId)
        } else {
            this.active--
        }
        request.reject(new DOMException("AbortError", "AbortError"))
        this.drain()
    }

    private readonly pendingById = new Map<RequestId, PendingRequest<TKinds>>()
    private readonly queue = new PriorityQueue<RequestId>()
    private nextRequestId = 1
    private maxConcurrent = 64
    private active = 0
}
